import React, { useEffect, useState } from "react";
import type { WritingTocItem } from "./writingUtils";

type WritingTableOfContentsProps = {
  items: WritingTocItem[];
};

const SLOT = 24;
const HALF_SLOT = SLOT / 2;
const HIT = 36;
const OVERHANG = (HIT - SLOT) / 2;
const BAR_W_H2 = 20;
const BAR_W_H3 = 12;
const DEPTH3_INDENT = 8;

// The first TOC item ("writing-title") maps to the intro section, which carries
// the title; every other item id matches its section's data-section-id.
const sectionIdForItem = (id: string) => (id === "writing-title" ? "intro" : id);

const WritingTableOfContents: React.FC<WritingTableOfContentsProps> = ({ items }) => {
  const [activeId, setActiveId] = useState("");
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [pulseKey, setPulseKey] = useState(0);
  // Per-item reading progress (0–1) used by the mobile story-style bars.
  const [progress, setProgress] = useState<number[]>([]);

  useEffect(() => {
    if (!items.length) return;

    setActiveId(items[0].id);

    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible?.target.id) {
          setActiveId(visible.target.id);
        }
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 }
    );

    items.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [items]);

  const hasItems = items.length > 0;
  const targetId = hoveredId ?? activeId;
  const targetIndex = hasItems ? items.findIndex((item) => item.id === targetId) : -1;
  const targetDepth = targetIndex >= 0 ? items[targetIndex].depth ?? 2 : 2;

  useEffect(() => {
    if (targetIndex < 0) return;
    setPulseKey((k) => k + 1);
  }, [targetIndex, targetDepth]);

  // Compute how far the reader has scrolled through each section so the mobile
  // bars can fill like Instagram-story segments while content scrolls normally.
  useEffect(() => {
    if (!items.length) return;

    let frame = 0;
    const compute = () => {
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const doc = document.documentElement;
      const atBottom = scrollY + viewportHeight >= doc.scrollHeight - 2;

      const next = items.map(({ id }, index) => {
        const element = document.querySelector(
          `[data-section-id="${CSS.escape(sectionIdForItem(id))}"]`
        );
        if (!(element instanceof HTMLElement) || element.offsetHeight === 0) return 0;

        // Fraction of the section the viewport's top edge has scrolled past.
        const ratio = (scrollY - element.offsetTop) / element.offsetHeight;
        const filled = Math.min(1, Math.max(0, ratio));

        // The final section can't scroll fully off the top, so complete it once
        // the page is scrolled to the bottom.
        if (atBottom && index === items.length - 1) return 1;
        return filled;
      });

      setProgress((current) => {
        const unchanged =
          current.length === next.length && current.every((value, i) => value === next[i]);
        return unchanged ? current : next;
      });
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        compute();
      });
    };

    compute();
    // Recompute as layout settles (fonts, view transitions) so bars aren't stale.
    const settleTimers = [0, 120, 360].map((delay) => window.setTimeout(compute, delay));
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", compute);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", compute);
      settleTimers.forEach((timer) => window.clearTimeout(timer));
      if (frame) cancelAnimationFrame(frame);
    };
  }, [items]);

  if (!hasItems) return null;

  const jumpTo = (id: string) => {
    const section = document.getElementById(id);
    if (!section) return;

    section.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
    setActiveId(id);
  };

  return (
    <>
    <nav aria-label="Reading progress" className="writing-toc-mobile">
      {items.map(({ id, text: label }, index) => (
        <button
          key={`mobile-${id}`}
          type="button"
          onClick={() => jumpTo(id)}
          className="writing-toc-mobile__seg"
          aria-label={`Jump to ${label}`}
          aria-current={activeId === id ? "location" : undefined}
        >
          <span
            aria-hidden="true"
            className="writing-toc-mobile__fill"
            style={{ transform: `scaleX(${progress[index] ?? 0})` }}
          />
        </button>
      ))}
    </nav>

    <nav
      aria-label="Table of contents"
      className="writing-toc group"
      onMouseLeave={() => setHoveredId(null)}
    >
      <div
        className="writing-toc__rail relative w-8 shrink-0"
        style={{ height: items.length > 0 ? (items.length - 1) * SLOT + HIT : 0 }}
      >
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute left-0 top-0 h-0.5 rounded-full transition-[transform,width,opacity] duration-300 ease-out`}
          style={{
            width: targetDepth === 3 ? BAR_W_H3 : BAR_W_H2,
            opacity: targetIndex >= 0 ? 1 : 0,
            transform: `translate(${targetDepth === 3 ? DEPTH3_INDENT : 0}px, ${
              HALF_SLOT + SLOT * Math.max(targetIndex, 0)
            }px) translateY(-50%)`,
          }}
        >
          <span
            // Key forces the pulse animation to restart each hop without
            // interfering with the outer translate transition.
            key={pulseKey}
            className="writing-toc__accent-pulse block h-full w-full rounded-full bg-accent"
          />
        </span>

        {items.map(({ id, text: label, depth = 2 }, index) => {
          const isTarget = targetId === id;
          const barWidth = depth === 3 ? BAR_W_H3 : BAR_W_H2;

          return (
            <a
              key={id}
              href={`#${id}`}
              onClick={(event) => {
                event.preventDefault();
                jumpTo(id);
              }}
              onMouseEnter={() => setHoveredId(id)}
              onFocus={() => setHoveredId(id)}
              onBlur={() => setHoveredId(null)}
              className="absolute left-0 flex h-9 w-8 items-center"
              style={{
                top: index * SLOT - OVERHANG,
                paddingLeft: depth === 3 ? DEPTH3_INDENT : 0,
              }}
              aria-current={activeId === id && !hoveredId ? "location" : undefined}
            >
              <span
                aria-hidden="true"
                className={`h-0.5 shrink-0 rounded-full bg-track transition-opacity duration-300 ${
                  isTarget ? "opacity-0" : "opacity-100"
                }`}
                style={{ width: barWidth }}
              />
              <span className="sr-only">{label}</span>
            </a>
          );
        })}
      </div>

      <div className="writing-toc__popover pointer-events-none absolute left-full top-1/2 z-10 ml-4 w-56 -translate-y-1/2 rounded-md border border-subtle bg-surface-elevated px-3 py-2.5 opacity-0 shadow-elevated transition-opacity duration-200 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
        <div className="flex flex-col gap-4">
          {items.map(({ id, text: label, depth = 2 }) => (
            <a
              key={`tooltip-${id}`}
              href={`#${id}`}
              onClick={(event) => {
                event.preventDefault();
                jumpTo(id);
              }}
              onMouseEnter={() => setHoveredId(id)}
              onFocus={() => setHoveredId(id)}
              onBlur={() => setHoveredId(null)}
              className={`block text-sm leading-4 transition-colors ${
                targetId === id ? "text-primary" : "text-tertiary"
              } ${depth === 3 ? "pl-4 text-[13px]" : ""}`}
            >
              {label}
            </a>
          ))}
        </div>
      </div>
    </nav>
    </>
  );
};

export default WritingTableOfContents;
